import { createApi } from '@reduxjs/toolkit/query/react';
import { createParser, type ParsedEvent } from 'eventsource-parser';
import { castDraft } from 'immer';

import fetchBaseQuery, { getCsrfHeader } from '../fetchBaseQuery';
import type {
    ContextRequest,
    ContextResponse,
    ContextUpdateRequest,
    HistoryMessage,
    ReportFeedbackRequest,
    TranslateRequest,
    TranslateResponse,
    TranslationMessage,
} from './types';

export * from './types';

const handleStream = async ({
    request,
    onEvent,
}: {
    request: Promise<Response>;
    onEvent: (event: ParsedEvent) => void;
}) => {
    const response = await request;

    if (
        response.status === 200 &&
        response.headers.get('content-type')?.startsWith('text/event-stream') &&
        response.body
    ) {
        const parser = createParser((event) => {
            if (event.type !== 'event') {
                return;
            }

            onEvent(event);
        });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        // eslint-disable-next-line no-constant-condition
        while (true) {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();
            if (done) break;
            parser.feed(decoder.decode(value, { stream: true }));
        }
    }
};

let counter = 0;

const aiApi = createApi({
    reducerPath: 'lokaliseInternal_ai',
    baseQuery: fetchBaseQuery(),
    tagTypes: ['Context'],
    endpoints: (builder) => ({
        translate: builder.query<TranslateResponse, TranslateRequest>({
            queryFn: async () => ({
                data: { result: { variants: [], history: [] }, isStreaming: true },
            }),
            onCacheEntryAdded: async (args, { cacheDataLoaded, updateCachedData }) => {
                try {
                    // wait for the initial query to resolve before proceeding
                    await cacheDataLoaded;

                    await handleStream({
                        request: fetch('/ai/translate', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Accept: 'text/event-stream',
                                'X-CSRF-Token': getCsrfHeader(),
                            },
                            signal: window.streamAbortController.signal,
                            body: JSON.stringify(args),
                        }),
                        onEvent: (event) => {
                            if (event.event === 'translation') {
                                const message = JSON.parse(event.data) as TranslationMessage;
                                updateCachedData((draft) => {
                                    const id = parseInt(message.id, 10);
                                    if (!draft.result.variants[id]) {
                                        castDraft(draft.result.variants)[id] = {
                                            id: message.id,
                                            content: '',
                                            isComplete: false,
                                        };
                                    }
                                    draft.result.variants[id].content += message.content;
                                    draft.result.variants[id].isComplete = message.isComplete;
                                });
                            }
                            if (event.event === 'history') {
                                const newHistory = JSON.parse(event.data) as HistoryMessage;
                                updateCachedData((draft) => {
                                    draft.result.history = castDraft(newHistory);
                                });
                            }
                        },
                    });
                } catch {
                    // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
                    // in which case `cacheDataLoaded` will throw
                }

                updateCachedData((draft) => {
                    draft.isStreaming = false;
                });
                // We increment counter after each request to ensure new cache value
                counter += 1;
            },
            // The counter ensures that each request is considered a new request and isn't reusing previously cached results.
            // Typically, we'd use mutation for this but with mutation we can't do result streaming.
            serializeQueryArgs: (args) => ({ ...args, counter }),
            // Always refetch
            forceRefetch: () => true,
        }),
        getContext: builder.query<ContextResponse, ContextRequest>({
            query: (params) => ({
                method: 'GET',
                url: '/ai/context',
                params,
            }),
            providesTags: (result, error, arg) => [{ type: 'Context', id: arg.keyId }],
        }),
        updateContext: builder.mutation<ContextResponse, ContextUpdateRequest>({
            query: (args) => ({
                method: 'POST',
                url: '/ai/context',
                body: args,
            }),
            async onQueryStarted({ segmentId, keyId, context }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    aiApi.util.updateQueryData('getContext', { segmentId, keyId }, (draft) => {
                        draft.context = context;
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                    // Force re-fetch context for the segment
                    dispatch(aiApi.util.invalidateTags([{ type: 'Context', id: keyId }]));
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'Context', id: arg.keyId }],
        }),
        reportFeedback: builder.mutation<void, ReportFeedbackRequest>({
            query: (args) => ({
                method: 'POST',
                url: '/ai/report-feedback',
                body: args,
            }),
        }),
    }),
});

export default aiApi;

export const {
    useTranslateQuery,
    useLazyTranslateQuery,
    useUpdateContextMutation,
    useGetContextQuery,
    useLazyGetContextQuery,
    useReportFeedbackMutation,
} = aiApi;
